import { useCallback } from 'react'

import { isEmpty } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { Circle, CloseRounded, InfoOutlineRounded } from '@mui/icons-material'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'

import { LoadingOverlay } from '@common/components'
import { parseApiErrors } from '@common/utils'
import deviceUrls from '@portal/pages/Devices/urls'
import unitUrls from '@portal/pages/Units/urls'

import { getLayerColor, getLayerFormattedValue } from '../utils'
import { LAYER_AWS_STATUS, LAYER_DATA } from './LayerSelector'

const PROVISIONER_MODE_HINT =
  "Provisioner Mode works on nearby devices that have bluetooth enabled. The selected device will attempt to provision any nearby devices with its own active credentials and populate the 'Operational State' and 'Firmware' layers for any devices it is able to. Provisioner Mode automatically turns off after 1 hour."

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object} props.data
 * @param {React.Node} props.data.anchor
 * @param {Object} props.data.unit
 * @param {string} props.layer
 * @param {Function} props.onProvisionerModeEnabled
 * @param {Function} props.onClose
 */
export default function DetailPopover({
  data,
  layer,
  legend,
  onProvisionerModeEnabled,
  onClose,
}) {
  const {
    connectivityMapDataIsLoading,
    deviceEnableProvisionerModeIsLoading,
    doDeviceEnableProvisionerMode,
    doShowSnackbar,
  } = useConnect(
    'selectConnectivityMapDataIsLoading',
    'selectDeviceEnableProvisionerModeIsLoading',
    'doDeviceEnableProvisionerMode',
    'doShowSnackbar',
  )

  const enableProvisionerMode = useCallback(async ({ id, mac }) => {
    try {
      await doDeviceEnableProvisionerMode(id)
      await onProvisionerModeEnabled()
      doShowSnackbar(`Provisioner mode for ${mac} successfully enabled`, 'success')
    } catch (err) {
      const parsedError = parseApiErrors(err?.response)
      doShowSnackbar(parsedError, 'error')
    }
  }, [])

  return (
    <Popover
      open={!!data?.anchor}
      anchorEl={data?.anchor}
      anchorOrigin={{ vertical: 40, horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      onClose={onClose}
      slotProps={{ backdrop: { invisible: true } }}
    >
      {data?.unit && (
        <Box sx={{ my: 1 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={4}
            sx={{ mx: 1.5 }}
          >
            <Link href={unitUrls.entity.replace(':id', data.unit.id)}>
              <Typography variant="h6">Room {data?.unit?.name}</Typography>
            </Link>
            <IconButton size="small" onClick={onClose}>
              <CloseRounded />
            </IconButton>
          </Box>
          <Divider sx={{ my: 1 }} />
          <LoadingOverlay loading={connectivityMapDataIsLoading}>
            {!isEmpty(data.unit.devices) ? (
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    '& tbody tr:last-child td': { border: 0 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Device</TableCell>
                      <TableCell align="left">{LAYER_DATA[layer].label}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Tooltip title={PROVISIONER_MODE_HINT}>
                            <InfoOutlineRounded
                              sx={{ fontSize: 16, color: 'primary.main' }}
                            />
                          </Tooltip>
                          Provisioner Mode
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.unit?.devices.map((device) => {
                      const inProvisionerMode =
                        device?.connectivityGuidanceMode === 'PROVISIONER_MODE'
                      const isAWSOffline = device?.awsStatus === 'OFFLINE'

                      let buttonTooltip
                      if (inProvisionerMode) {
                        buttonTooltip = 'Device already in provisioner mode'
                      } else if (isAWSOffline) {
                        buttonTooltip =
                          'Device should be online to enable provisioner mode'
                      }

                      return (
                        <TableRow key={device.mainMac}>
                          <TableCell align="left">
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Tooltip title={`AWS Status: ${device.awsStatus}`}>
                                <Circle
                                  sx={{
                                    fontSize: 10,
                                    color: getLayerColor({
                                      unit: data.unit,
                                      device,
                                      layer: LAYER_AWS_STATUS,
                                      legend,
                                    }),
                                  }}
                                />
                              </Tooltip>
                              <Link href={deviceUrls.entity.replace(':id', device.id)}>
                                {device.mainMac}
                              </Link>
                            </Box>
                          </TableCell>
                          <TableCell align="left">
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Circle
                                sx={{
                                  fontSize: 10,
                                  color: getLayerColor({
                                    unit: data.unit,
                                    device,
                                    layer,
                                    legend,
                                  }),
                                }}
                              />
                              {getLayerFormattedValue({
                                unit: data.unit,
                                device,
                                layer,
                              })}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={buttonTooltip}>
                              <span>
                                <Button
                                  loading={deviceEnableProvisionerModeIsLoading}
                                  disabled={inProvisionerMode || isAWSOffline}
                                  variant="contained"
                                  size="small"
                                  onClick={() =>
                                    enableProvisionerMode({
                                      id: device.id,
                                      mac: device.mainMac,
                                    })
                                  }
                                >
                                  Enable
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography sx={{ mx: 1.5, mb: 1.5, textAlign: 'center', color: 'grey' }}>
                No Devices Found
              </Typography>
            )}
          </LoadingOverlay>
        </Box>
      )}
    </Popover>
  )
}
